import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, MapPin, Calendar, CurrencyDollar, Briefcase, Plus, Trash,
  CheckCircle, XCircle, PaperPlaneTilt, Paperclip, FilePdf, WarningCircle,
  Trophy, Medal, CaretDown, CaretUp, ArrowsClockwise,
} from "@phosphor-icons/react";
import DatePicker from "@/components/DatePicker";
import AttachmentUploader from "@/components/AttachmentUploader";
import VerifiedBadge from "@/components/VerifiedBadge";
import { loadRates, convertFromUsd, currencySymbol } from "@/lib/fx";

/* v2.91 · Job detail — 3 sections: header, proposals, messages.
   Role-aware: client owns → sees all proposals + can accept/reject.
   PE → sees only their own proposal + can submit if none exists.
   Messaging visible to job owner + any PE with a submitted proposal. */

const emptyProposal = () => ({
  cover_letter: "",
  scope_items: [""],
  itemized_scope: [],   // v3.2.14 · pre-filled from /proposals/scope-template
  milestones: [{ title: "", description: "", amount_usd: 0, due_date: "" }],
  deliverables: [{ name: "", description: "" }],
  timeline_weeks: 0,
  total_fee_usd: 0,
  terms_accepted: false,
  attachments: [],
});

function fmtUsd(n) {
  if (!n || Number.isNaN(Number(n))) return "$0";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/* v3.2.8 · "$1,000 ≈ €925" — appends the local-currency equivalent
   next to any USD amount when the client's currency differs.

   v3.2.9 · Optional third arg is the proposal's `fx_lock` snapshot; when
   present AND its currency matches the current display currency, the
   locked rate is used instead of the live rate so cross-border quotes
   never drift after submission. */
function fmtUsdWithLocal(n, currency, fxLock) {
  const usd = fmtUsd(n);
  const cur = String(currency || "USD").toUpperCase();
  if (!n || cur === "USD") return usd;
  let local = null;
  if (fxLock && String(fxLock.currency || "").toUpperCase() === cur && Number(fxLock.rate) > 0) {
    local = Number(n) * Number(fxLock.rate);
  } else {
    local = convertFromUsd(n, cur);
  }
  if (local == null) return usd;
  return `${usd} ≈ ${currencySymbol(cur)}${Number(local).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/* ─── Proposal Form ────────────────────────────────────────────── */

function ProposalForm({ jobId, initial, onSaved }) {
  const [form, setForm] = useState(initial || emptyProposal());
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState({ jr: 0, sr: 0 });   // v3.2.14 · PE billing rates
  const isEditing = Boolean(initial?.id);

  // v3.2.14 · Pre-fill the 10 standard scope rows + snapshot rates.
  // v3.2.62 · Always fetch rates so edit mode also drives the derived
  // Cost column + accurate "rates configured" caption. Only skip the
  // row-template prefill when we're editing an existing proposal.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/proposals/scope-template");
        setRates({ jr: Number(data.jr_rate_usd) || 0, sr: Number(data.sr_rate_usd) || 0 });
        const alreadyHasRows = (form.itemized_scope || []).length > 0;
        if (!isEditing && !alreadyHasRows) {
          setForm((s) => ({ ...s, itemized_scope: data.rows || [] }));
        }
      } catch { /* silent — form still works */ }
    })();
  }, []);

  const patch = (p) => setForm((s) => ({ ...s, ...p }));
  const updItem = (key, i, patch2) => {
    const list = [...form[key]]; list[i] = { ...list[i], ...patch2 };
    setForm({ ...form, [key]: list });
  };

  // v3.2.14 · Auto-compute cost per row from Jr/Sr hrs × configured rates,
  // and roll the row totals up into `total_fee_usd`.
  const updScopeRow = (i, patch2) => {
    const list = [...(form.itemized_scope || [])];
    const merged = { ...list[i], ...patch2 };
    const jrH = Number(merged.jr_hrs) || 0;
    const srH = Number(merged.sr_hrs) || 0;
    if (rates.jr > 0 || rates.sr > 0) {
      // Rates configured → cost is derived (rows become read-only for cost).
      merged.cost = (jrH * rates.jr) + (srH * rates.sr);
    } else if (patch2.cost === undefined) {
      // No rates configured → cost stays manually editable, keep old value.
      merged.cost = Number(merged.cost) || 0;
    }
    list[i] = merged;
    // v3.2.62 · Exclude empty-name rows so the cached total matches
    // what the submit filter + display show.
    const total = list
      .filter((r) => (r.item || "").trim())
      .reduce((acc, r) => acc + (Number(r.cost) || 0), 0);
    setForm({ ...form, itemized_scope: list, total_fee_usd: total });
  };

  const submit = async () => {
    // v3.2.62 · Full pre-flight validation. Previously only cover
    // letter + T&C were checked, so PEs could submit proposals with
    // zero timeline, zero fee, and no milestones and hit an opaque
    // backend error.
    if ((form.cover_letter || "").trim().length < 10) {
      toast.error("Cover letter must be at least 10 characters.");
      return;
    }
    const scopeRows = (form.itemized_scope || []).filter((r) => (r.item || "").trim());
    const scopeTotal = scopeRows.reduce((a, r) => a + (Number(r.cost) || 0), 0);
    if (scopeRows.length === 0) {
      toast.error("Add at least one itemized scope row.");
      return;
    }
    if (!(scopeTotal > 0)) {
      toast.error("Itemized scope total must be greater than $0. Enter hours or a cost per row.");
      return;
    }
    const milestones = (form.milestones || []).filter((m) => (m.title || "").trim());
    if (milestones.length === 0) {
      toast.error("Add at least one milestone.");
      return;
    }
    const badMs = milestones.find((m) =>
      !(m.title || "").trim() ||
      !(Number(m.amount_usd) > 0) ||
      !(m.due_date || "").trim());
    if (badMs) {
      toast.error("Every milestone needs a title, an amount due, and a due date.");
      return;
    }
    const msSum = milestones.reduce((a, m) => a + (Number(m.amount_usd) || 0), 0);
    if (Math.round(msSum) !== Math.round(scopeTotal)) {
      toast.error(`Milestone amounts add up to $${msSum.toLocaleString()} but your itemized total is $${scopeTotal.toLocaleString()}. They must match.`);
      return;
    }
    if (!(Number(form.timeline_weeks) > 0)) {
      toast.error("Timeline must be at least 1 week.");
      return;
    }
    if (!form.terms_accepted) {
      toast.error("Accept the T&Cs to submit.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        scope_items: form.scope_items.filter((s) => s.trim()),
        milestones,
        deliverables: form.deliverables.filter((d) => d.name.trim()),
        // Total fee is now DERIVED from the itemized scope — the input
        // is a read-only mirror. Recompute here to stay in sync even
        // if the state got out-of-date.
        total_fee_usd:  scopeTotal,
        timeline_weeks: Number(form.timeline_weeks) || 0,
      };
      if (isEditing) {
        await api.patch(`/jobs/${jobId}/proposals/${initial.id}`, payload);
        toast.success("Proposal updated.");
      } else {
        await api.post(`/jobs/${jobId}/proposals`, payload);
        toast.success("Proposal submitted.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#00E5FF]/30 bg-[#12151C] p-5 space-y-4" data-testid="proposal-form">
      <div className="label-tag">// {isEditing ? "edit proposal" : "submit rfp proposal"}</div>
      <div>
        <div className="label-tag block mb-1">Cover letter</div>
        <textarea value={form.cover_letter}
          onChange={(e) => patch({ cover_letter: e.target.value })}
          rows={4}
          className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none resize-none"
          placeholder="Why you're the right fit + high-level approach." data-testid="proposal-cover" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="label-tag">Itemized scope of work</span>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono text-gray-500">
              {(rates.jr > 0 || rates.sr > 0)
                ? <>Rates: <b className="text-[#00E5FF]">Jr ${rates.jr}/hr</b> · <b className="text-[#00E5FF]">Sr ${rates.sr}/hr</b> · <a href="/settings" className="text-[#FFCC00] hover:underline">edit</a></>
                : <>No billing rates set — <a href="/settings" className="text-[#FFCC00] hover:underline">configure rates</a> or enter Cost manually.</>}
            </span>
            <button type="button"
              onClick={() => patch({ itemized_scope: [...(form.itemized_scope || []),
                { item: "", jr_hrs: 0, sr_hrs: 0, cost: 0 }] })}
              className="text-xs text-[#00E5FF] hover:underline font-mono"
              data-testid="proposal-scope-add">
              <Plus size={12} className="inline" /> add
            </button>
          </div>
        </div>
        <div className="border border-white/10 overflow-hidden" data-testid="proposal-itemized-scope">
          <div className="grid grid-cols-12 bg-black/50 text-[10px] font-mono uppercase tracking-wider text-gray-500 px-2 py-1.5">
            <div className="col-span-4">Item</div>
            <div className="col-span-2 text-right">Jr/Int hrs</div>
            <div className="col-span-2 text-right">Sr hrs</div>
            <div className="col-span-3 text-right">Cost (USD)</div>
            <div className="col-span-1" />
          </div>
          {(form.itemized_scope || []).map((row, i) => {
            const derived = (rates.jr > 0 || rates.sr > 0);
            return (
              <div key={i} className="grid grid-cols-12 gap-1 items-center px-2 py-1 border-t border-white/5"
                   data-testid={`proposal-scope-row-${i}`}>
                <input value={row.item || ""}
                  onChange={(e) => updScopeRow(i, { item: e.target.value })}
                  className="col-span-4 bg-transparent border-none px-1 py-1 text-xs font-mono text-white focus:outline-none focus:bg-black/40"
                  data-testid={`proposal-scope-item-${i}`} />
                <input type="number" min={0} step={0.5} value={row.jr_hrs || ""}
                  onChange={(e) => updScopeRow(i, { jr_hrs: Number(e.target.value) })}
                  className="col-span-2 bg-black/30 border border-white/5 px-2 py-1 text-xs font-mono text-right focus:border-[#00E5FF] outline-none"
                  data-testid={`proposal-scope-jr-${i}`} />
                <input type="number" min={0} step={0.5} value={row.sr_hrs || ""}
                  onChange={(e) => updScopeRow(i, { sr_hrs: Number(e.target.value) })}
                  className="col-span-2 bg-black/30 border border-white/5 px-2 py-1 text-xs font-mono text-right focus:border-[#00E5FF] outline-none"
                  data-testid={`proposal-scope-sr-${i}`} />
                <input type="number" min={0} step={100}
                  value={
                    derived
                      ? (Number(row.cost) > 0 ? Number(row.cost).toFixed(0) : "")
                      : (row.cost || "")
                  }
                  onChange={(e) => updScopeRow(i, { cost: Number(e.target.value) })}
                  readOnly={derived}
                  placeholder={derived ? "$0" : "Cost"}
                  className={`col-span-3 border border-white/5 px-2 py-1 text-xs font-mono text-right focus:border-[#00E5FF] outline-none ${derived ? "bg-black/50 text-emerald-400" : "bg-black/30 text-white"}`}
                  data-testid={`proposal-scope-cost-${i}`} />
                <button type="button"
                  onClick={() => {
                    const next = (form.itemized_scope || []).filter((_, idx) => idx !== i);
                    const total = next
                      .filter((r) => (r.item || "").trim())
                      .reduce((a, r) => a + (Number(r.cost) || 0), 0);
                    setForm({ ...form, itemized_scope: next, total_fee_usd: total });
                  }}
                  className="col-span-1 flex items-center justify-center text-gray-500 hover:text-[#FF3B30]"
                  aria-label={`Remove scope row ${i + 1}`}
                  data-testid={`proposal-scope-del-${i}`}>
                  <Trash size={12} />
                </button>
              </div>
            );
          })}
          <div className="grid grid-cols-12 bg-[#FFCC00]/10 text-xs font-mono px-2 py-1.5 border-t border-[#FFCC00]/40"
               data-testid="proposal-scope-total">
            <div className="col-span-4 text-[#FFCC00] font-bold">Total</div>
            <div className="col-span-2 text-right text-gray-400">
              {(form.itemized_scope || []).filter((r) => (r.item || "").trim()).reduce((a, r) => a + (Number(r.jr_hrs) || 0), 0)}
            </div>
            <div className="col-span-2 text-right text-gray-400">
              {(form.itemized_scope || []).filter((r) => (r.item || "").trim()).reduce((a, r) => a + (Number(r.sr_hrs) || 0), 0)}
            </div>
            <div className="col-span-3 text-right text-[#FFCC00] font-bold">
              ${(form.itemized_scope || []).filter((r) => (r.item || "").trim()).reduce((a, r) => a + (Number(r.cost) || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="col-span-1" />
          </div>
        </div>
        <div className="text-[10px] font-mono text-gray-500 mt-1">
          Cost auto-fills from your rates. Adjust hours per role and the total fee updates automatically.
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="label-tag">Milestones</span>
          <button onClick={() => patch({ milestones: [...form.milestones, { title: "", description: "", amount_usd: 0, due_date: "" }] })}
            className="text-xs text-[#00E5FF] hover:underline font-mono"><Plus size={12} className="inline" /> add</button>
        </div>
        {form.milestones.map((m, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-1 mb-1" data-testid={`proposal-ms-row-${i}`}>
            <input value={m.title} onChange={(e) => updItem("milestones", i, { title: e.target.value })}
              placeholder="Milestone"
              className="md:col-span-4 bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none" data-testid={`proposal-ms-title-${i}`} />
            <input value={m.description} onChange={(e) => updItem("milestones", i, { description: e.target.value })}
              placeholder="Description"
              className="md:col-span-4 bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none" />
            <input type="number" min={0}
              value={m.amount_usd > 0 ? m.amount_usd : ""}
              onChange={(e) => updItem("milestones", i, { amount_usd: Number(e.target.value) })}
              placeholder="Amount due"
              className="md:col-span-2 bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none" data-testid={`proposal-ms-amount-${i}`} />
            <div className="md:col-span-2">
              <DatePicker value={m.due_date}
                onChange={(v) => updItem("milestones", i, { due_date: v })}
                placeholder="due date"
                testid={`proposal-ms-due-${i}`} />
            </div>
          </div>
        ))}
        {/* v3.2.63 · Live milestone-sum vs scope-total indicator so
             the PE catches mismatches BEFORE the submit-time toast. */}
        {(() => {
          const scopeTotal = (form.itemized_scope || [])
            .filter((r) => (r.item || "").trim())
            .reduce((a, r) => a + (Number(r.cost) || 0), 0);
          const msSum = (form.milestones || [])
            .reduce((a, m) => a + (Number(m.amount_usd) || 0), 0);
          const matches = Math.round(msSum) === Math.round(scopeTotal);
          const anyMilestoneAmount = msSum > 0;
          if (!anyMilestoneAmount && scopeTotal <= 0) return null;
          return (
            <div className={`mt-1 flex justify-between text-[10px] font-mono border-t ${matches ? "border-emerald-500/30" : "border-[#FF8A85]/40"} pt-1`}
                  data-testid="proposal-ms-total">
              <span className="text-gray-500">
                Total amount due must match itemized scope total
              </span>
              <span className={matches ? "text-emerald-400" : "text-[#FF8A85]"}>
                ${msSum.toLocaleString()} / ${scopeTotal.toLocaleString()}{" "}
                {matches ? "✓" : "· doesn't match"}
              </span>
            </div>
          );
        })()}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="label-tag">Deliverables</span>
          <button onClick={() => patch({ deliverables: [...form.deliverables, { name: "", description: "" }] })}
            className="text-xs text-[#00E5FF] hover:underline font-mono"><Plus size={12} className="inline" /> add</button>
        </div>
        {form.deliverables.map((d, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-1 mb-1">
            <input value={d.name} onChange={(e) => updItem("deliverables", i, { name: e.target.value })}
              placeholder="Deliverable name"
              className="md:col-span-4 bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none" data-testid={`proposal-del-${i}`} />
            <input value={d.description} onChange={(e) => updItem("deliverables", i, { description: e.target.value })}
              placeholder="Notes"
              className="md:col-span-8 bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="label-tag block mb-1">Timeline (weeks)</div>
          <input type="number" min={0} value={form.timeline_weeks}
            onChange={(e) => patch({ timeline_weeks: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" data-testid="proposal-weeks" />
        </div>
        <div>
          <div className="label-tag block mb-1">Total fee (USD)</div>
          <input type="number" readOnly
            value={(form.itemized_scope || [])
              .filter((r) => (r.item || "").trim())
              .reduce((a, r) => a + (Number(r.cost) || 0), 0).toFixed(0)}
            className="w-full bg-black/60 border border-white/10 px-3 py-2 text-sm font-mono text-emerald-400 focus:outline-none cursor-not-allowed" data-testid="proposal-fee" />
          <div className="text-[10px] font-mono text-gray-500 mt-1">
            Auto-copied from your itemized scope total (empty-name rows excluded). Adjust hours or costs above to change this. The rate is <b className="text-[#FFCC00]">locked at submission</b>, so FX drift won&apos;t change your quote.
          </div>
        </div>
      </div>

      <div>
        <span className="label-tag block mb-2">Attachments (optional)</span>
        <AttachmentUploader
          endpoint="/proposals/upload-attachment"
          kind="proposal"
          value={form.attachments || []}
          onChange={(next) => patch({ attachments: next })}
          testid="proposal-attach" />
      </div>

      <label className="flex items-start gap-2 text-xs text-gray-300">
        <input type="checkbox" checked={form.terms_accepted}
          onChange={(e) => patch({ terms_accepted: e.target.checked })}
          className="accent-[#00E5FF] mt-1"
          data-testid="proposal-terms" />
        <span>I accept the platform T&amp;Cs — professional-services engagement, non-refundable milestones, and standard IP handover after final acceptance.</span>
      </label>

      <div className="flex justify-end">
        <button onClick={submit} disabled={busy}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="proposal-submit">
          <PaperPlaneTilt size={16} weight="fill" /> {busy ? "Sending…" : isEditing ? "Update proposal" : "Submit proposal"}
        </button>
      </div>
    </div>
  );
}

/* ─── Message Thread ───────────────────────────────────────────── */

function MessageThread({ jobId, myId }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/messages`);
      setMsgs(data);
    } catch (e) {
      // 403 is expected for non-participants — silently skip
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);
  // v2.91 · 8-second polling for near-real-time chat.
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs]);

  const send = async () => {
    if (!body.trim() && !file) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("body", body || "");
      if (file) form.append("file", file);
      await api.post(`/jobs/${jobId}/messages`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setBody(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <section className="mt-6 border border-white/10 bg-[#12151C] p-5" data-testid="job-messages">
      <div className="label-tag mb-3">// messages</div>
      <div ref={listRef} className="max-h-80 overflow-y-auto space-y-3 pr-1" data-testid="job-messages-list">
        {msgs.length === 0 && (
          <div className="text-xs text-gray-500 font-mono">No messages yet. Say hi.</div>
        )}
        {msgs.map((m) => {
          const mine = m.sender_id === myId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] border ${mine ? "border-[#00E5FF]/30 bg-[#00E5FF]/5" : "border-white/10 bg-black/40"} p-2.5`} data-testid={`job-message-${m.id}`}>
                <div className="text-[10px] font-mono text-gray-500">
                  <span className={mine ? "text-[#00E5FF]" : "text-gray-400"}>{m.sender_name}</span>
                  {m.sender_incognito && (
                    <span className="ml-1 inline-flex items-center gap-0.5 border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-1 py-0 text-[9px] font-mono uppercase tracking-widest text-[#00E5FF]"
                          title="Real name will be revealed once you award this engineer the job."
                          data-testid={`job-message-incognito-${m.id}`}>
                      Incognito
                    </span>
                  )}
                  {" · "}{new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
                {m.body && <div className="text-sm mt-1 whitespace-pre-wrap">{m.body}</div>}
                {(m.attachments || []).map((a) => (
                  <a key={a.ref}
                     href={`${getBackendUrl()}/api/jobs/messages/attachments/${a.ref}`}
                     target="_blank" rel="noreferrer"
                     className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#00E5FF] hover:underline font-mono"
                     data-testid={`job-message-attach-${a.ref}`}>
                    {a.content_type?.startsWith("image/")
                      ? <img src={`${getBackendUrl()}/api/jobs/messages/attachments/${a.ref}`} alt="" className="max-w-[220px] max-h-40 object-cover block mt-1" />
                      : <><FilePdf size={12} /> {a.filename}</>}
                  </a>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-start gap-2">
        <textarea value={body} onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          rows={2}
          className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none resize-none"
          placeholder="Type a message… (⌘/Ctrl+Enter to send)"
          data-testid="job-message-input" />
        <div className="flex flex-col gap-1">
          <input type="file" ref={fileRef} accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" hidden
            onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="job-message-file" />
          <button onClick={() => fileRef.current?.click()} title="Attach a PDF or image"
            className={`border border-white/20 px-2 py-2 text-sm font-mono hover:bg-white/5 ${file ? "text-[#00E5FF]" : ""}`}
            data-testid="job-message-attach-btn"><Paperclip size={16} /></button>
          <button onClick={send} disabled={sending}
            className="btn-primary px-3 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="job-message-send">
            <PaperPlaneTilt size={14} weight="fill" /> Send
          </button>
        </div>
      </div>
      {file && (
        <div className="text-[10px] text-[#00E5FF] font-mono mt-1">Attachment ready: {file.name}</div>
      )}
    </section>
  );
}

/* ─── Main page ────────────────────────────────────────────────── */

export default function JobDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  // v3.2.14 · One-click apply — when the URL carries ?apply=template,
  // pull the PE's most-recent submitted proposal and pre-fill the form
  // so they can just review-and-send.
  const [templateSeed, setTemplateSeed] = useState(null);

  const load = useCallback(async () => {
    try {
      const [j, p] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/jobs/${id}/proposals`),
      ]);
      setJob(j.data); setProposals(p.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Not found");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRates(); }, []);   // v3.2.8 · prime FX cache

  // v3.2.14 · If URL has ?apply=template, seed a fresh proposal from
  // the PE's most-recently-submitted proposal on any other job.
  useEffect(() => {
    if (!user || user.role !== "pe") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("apply") !== "template") return;
    (async () => {
      try {
        // Reuse the PE-applied bucket: it returns jobs where they have a proposal.
        const { data: applied } = await api.get("/jobs?bucket=applied&sort=date&order=desc&limit=1");
        if (!applied?.length) { toast.info("No previous proposal to copy from."); return; }
        const { data: props } = await api.get(`/jobs/${applied[0].id}/proposals`);
        const mine = props.find((p) => p.pe_id === user.id);
        if (!mine) return;
        setTemplateSeed({
          cover_letter:   mine.cover_letter || "",
          scope_items:    mine.scope_items || [""],
          itemized_scope: mine.itemized_scope || [],
          milestones:     mine.milestones || [{ title: "", description: "", amount_usd: 0, due_date: "" }],
          deliverables:   mine.deliverables || [{ name: "", description: "" }],
          timeline_weeks: mine.timeline_weeks || 0,
          total_fee_usd:  mine.total_fee_usd  || 0,
          terms_accepted: false,
          attachments:    [],
        });
        setShowForm(true);
        toast.success("Loaded template from your last proposal — review and send.");
      } catch { /* silent */ }
    })();
  }, [user, id]);

  const decide = async (pid, kind) => {
    try {
      await api.post(`/jobs/${id}/proposals/${pid}/${kind}`);
      toast.success(kind === "accept" ? "Proposal accepted — job is now in progress." : "Proposal rejected.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  if (err) return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <button onClick={() => nav("/jobs")} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={14} /> jobs</button>
        <div className="mt-6 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4 text-sm text-[#FF8A85]" data-testid="job-detail-error">{err}</div>
      </div>
    </div>
  );
  if (!job) return <div className="industrial min-h-screen bg-[#0A0C10]"><Topbar /><div className="p-10 text-sm text-gray-500 font-mono">Loading…</div></div>;

  const role = user?.role;
  const myId = user?.id;
  const myProposal = proposals.find((p) => p.pe_id === myId);
  // v3.2.58 · Only PEs with an approved profile can bid. Mirrors the
  // authoritative backend guard in `jobs.py::create_proposal`.
  const peApproved = ["approved", "pending_revision"].includes(user?.pe_status);
  const canPropose = role === "pe" && peApproved && job.status === "open" && !myProposal;
  const canMessage = role === "admin" || (role === "client" && job.client_id === myId) || (role === "pe" && !!myProposal);

  // v3.2.56 · Delete job — admin (any state) + client owner (open only,
  // blocked if awarded / paid milestone / accepted proposal). Backend
  // also enforces these — the button is a UX guardrail only.
  const anyAccepted = proposals.some((p) => (p.status || "").toLowerCase() === "accepted");
  const canDeleteJob =
    !job.trashed_at &&
    (role === "admin" ||
      (role === "client" && job.client_id === myId &&
        !job.awarded_pe_id && !anyAccepted &&
        ["open", "cancelled", "completed"].includes(job.status)));
  const deleteJob = async () => {
    if (!window.confirm("Delete this job? It will be moved to trash and hidden from listings.")) return;
    try {
      await api.post(`/jobs/${id}/trash`);
      toast.success("Job deleted.");
      nav("/jobs");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  // v3.2.56 · Withdraw proposal — PE-only, on their own bid, if not accepted.
  const canWithdrawProposal =
    role === "pe" && myProposal &&
    !["accepted", "withdrawn"].includes((myProposal.status || "").toLowerCase());
  const withdrawProposal = async () => {
    if (!window.confirm("Withdraw your proposal? The client will still see it marked as WITHDRAWN.")) return;
    try {
      await api.post(`/jobs/${id}/proposals/${myProposal.id}/withdraw`);
      toast.success("Proposal withdrawn.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Withdraw failed");
    }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => nav("/jobs")} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="job-detail-back"><ArrowLeft size={14} /> jobs</button>
          {canDeleteJob && (
            <button onClick={deleteJob}
              className="text-xs font-mono uppercase tracking-wider text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-1 border border-white/10 hover:border-[#FF3B30]/50 px-3 py-1.5 transition"
              data-testid="job-detail-delete">
              <Trash size={12} /> Delete job
            </button>
          )}
        </div>

        {/* v3.2.33 · Dispute-open banner. Visible to the client + the
            awarded PE + admin so nobody is blindsided by a chargeback. */}
        {job.dispute && (
          <div className="mt-4 border-2 border-[#FF3B30] bg-[#FF3B30]/10 p-4"
               data-testid="job-dispute-banner">
            <div className="flex items-start gap-3">
              <div className="text-[#FF3B30]"><ArrowLeft size={0} /><span aria-hidden>⚠</span></div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-black text-[#FF3B30]"
                     data-testid="job-dispute-banner-title">
                  Chargeback dispute open · MS{job.dispute.milestone_idx}
                </div>
                <div className="mt-1 text-sm text-gray-200">
                  The card issuer has raised a dispute on the {job.dispute.currency}{" "}
                  {Number(job.dispute.amount || 0).toLocaleString()} payment for{" "}
                  <span className="font-bold">{job.dispute.milestone_title}</span>.
                  Funds may be held until this resolves.
                </div>
                {job.dispute.reason && (
                  <div className="mt-2 text-xs font-mono text-gray-400">
                    reason · {job.dispute.reason}
                  </div>
                )}
                <div className="mt-3 text-xs font-mono text-gray-400">
                  Contact <a href="/exchange" className="text-[#00E5FF] hover:underline">support</a> to submit evidence.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mt-4">
          <div className="label-tag">// {job.status}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black mt-1">{job.title}</h1>
          <div className="text-sm text-gray-400 mt-1">
            {job.client_company || job.client_name}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-xs font-mono text-gray-400">
            {job.location?.country && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {job.location.region ? `${job.location.region}, ${job.location.country}` : job.location.country}</span>}
            {job.budget_estimate_usd > 0 && <span className="inline-flex items-center gap-1 text-emerald-400" data-testid="job-detail-budget"><CurrencyDollar size={12} /> {fmtUsdWithLocal(job.budget_estimate_usd, job.client_currency)} budget</span>}
            {job.deadline && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {job.deadline}</span>}
            {job.proposals_count > 0 && <span className="inline-flex items-center gap-1 text-[#00E5FF]">{job.proposals_count} proposal{job.proposals_count === 1 ? "" : "s"}</span>}
            {job.awarded_pe_id && (role === "client" || role === "pe" || role === "admin") && (
              <a href="/exchange" className="inline-flex items-center gap-1 text-[#FFCC00] hover:underline"
                 data-testid="job-detail-deliverables-link">
                📁 open deliverables folder →
              </a>
            )}
          </div>
          <p className="mt-4 text-sm text-gray-200 whitespace-pre-wrap" data-testid="job-detail-description">{job.description}</p>
          {(job.scope || []).length > 0 && (
            <ul className="mt-4 space-y-1 text-sm text-gray-300 list-disc pl-5">
              {job.scope.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
          {(job.specializations_required || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1">
              {job.specializations_required.map((s) => (
                <span key={s} className="text-[10px] font-mono border border-[#00E5FF]/30 bg-[#00E5FF]/5 text-[#00E5FF] px-1.5 py-0.5">{s}</span>
              ))}
            </div>
          )}
          {(job.attachments || []).length > 0 && (
            <div className="mt-4">
              <div className="label-tag mb-2">// attachments</div>
              <AttachmentUploader
                endpoint="/jobs/upload-attachment"
                kind="job"
                value={job.attachments}
                onChange={() => {}}
                testid="job-detail-attach"
                readOnly />
            </div>
          )}
        </div>

        {/* PE actions */}
        {canPropose && !showForm && (
          <div className="mt-6">
            <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2" data-testid="job-detail-propose">
              <Briefcase size={16} weight="fill" /> Submit a proposal
            </button>
          </div>
        )}
        {/* v3.2.58 · Locked-out PE hint. Draft / pending / license_expired
            profiles are not on the directory yet, so we surface WHY
            they can't bid and where to unblock. */}
        {role === "pe" && !peApproved && job.status === "open" && !myProposal && (
          <div className="mt-6 border border-[#FFCC00]/30 bg-[#FFCC00]/[0.06] p-4 flex items-start gap-3"
                data-testid="job-detail-pe-not-approved">
            <WarningCircle size={20} weight="fill" className="text-[#FFCC00] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-bold">Waiting on admin approval</div>
              <div className="mt-1 text-xs font-mono text-gray-400 leading-relaxed">
                Your PE profile isn&apos;t live on the public directory yet, so proposals are locked.
                {user?.pe_status === "draft" && " Finish your profile and hit Submit for review."}
                {user?.pe_status === "pending" && " An admin is reviewing your submission — you&apos;ll be notified as soon as it lands."}
                {user?.pe_status === "license_expired" && " Your engineering license on file has lapsed. Upload a fresh copy from your profile."}
              </div>
              <Link to="/pe/profile"
                     className="mt-2 inline-block text-[11px] font-mono uppercase tracking-wider text-[#FFCC00] hover:underline">
                Open profile →
              </Link>
            </div>
          </div>
        )}
        {(canPropose && showForm) || (role === "pe" && myProposal && myProposal.status === "submitted") ? (
          <div className="mt-6">
            <ProposalForm jobId={id} initial={myProposal || templateSeed} onSaved={() => { setShowForm(false); load(); }} />
          </div>
        ) : null}
        {role === "pe" && myProposal && myProposal.status !== "submitted" && (
          <div className="mt-6 border border-white/10 bg-[#12151C] p-4 text-sm font-mono flex items-center justify-between gap-3 flex-wrap" data-testid="job-detail-my-status">
            <div>
              Your proposal: <span className={
                myProposal.status === "accepted" ? "text-emerald-400"
                : myProposal.status === "withdrawn" ? "text-gray-500"
                : "text-[#FF8A85]"
              }>{myProposal.status.toUpperCase()}</span>
              <span className="text-gray-500"> · {fmtUsdWithLocal(myProposal.total_fee_usd, job.client_currency, myProposal.fx_lock)} · {myProposal.timeline_weeks} wk</span>
            </div>
            {canWithdrawProposal && (
              <button onClick={withdrawProposal}
                className="text-[10px] font-mono uppercase tracking-wider text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-1 border border-white/10 hover:border-[#FF3B30]/50 px-2.5 py-1 transition"
                data-testid="proposal-withdraw-btn">
                <Trash size={11} /> Withdraw
              </button>
            )}
          </div>
        )}
        {role === "pe" && myProposal && myProposal.status === "submitted" && canWithdrawProposal && (
          <div className="mt-4">
            <button onClick={withdrawProposal}
              className="text-[10px] font-mono uppercase tracking-wider text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-1 border border-white/10 hover:border-[#FF3B30]/50 px-2.5 py-1 transition"
              data-testid="proposal-withdraw-btn">
              <Trash size={11} /> Withdraw this proposal
            </button>
          </div>
        )}

        {/* v3.2.17 · PE-side milestone board (read-only — approve/reject hidden by isClient gate). */}
        {role === "pe" && myProposal && myProposal.status === "accepted" && (myProposal.milestones || []).length > 0 && (
          <div className="mt-6" data-testid="pe-milestone-board-wrap">
            <MilestoneBoard jobId={id} milestones={myProposal.milestones} viewerRole={role} />
          </div>
        )}

        {/* Proposals list — client owner + admin (Leaderboard view) */}
        {(role === "admin" || (role === "client" && job.client_id === myId)) && proposals.length > 0 && (
          <ProposalLeaderboard
            jobId={id}
            jobStatus={job.status}
            currency={job.client_currency}
            proposals={proposals}
            viewerRole={role}
            onDecide={decide} />
        )}

        {/* Messages */}
        {canMessage && <MessageThread jobId={id} myId={myId} />}
      </div>
    </div>
  );
}

/* v3.2.5 · AI proposal scorer widget — client-only. Calls the LLM
   endpoint on first click, caches result on the backend, and renders
   a compact 3-rubric card + top 3 risks + a one-line recommendation. */
function AiScoreSection({ proposalId, initialScore }) {
  const { user } = useAuth();
  const [score, setScore] = useState(initialScore || null);
  const [busy, setBusy]   = useState(false);
  if (user?.role !== "client" && user?.role !== "admin") return null;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/${proposalId}/ai-score`);
      setScore(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI score failed");
    } finally { setBusy(false); }
  };
  const reRun = async () => {
    setBusy(true);
    try {
      await api.delete(`/proposals/${proposalId}/ai-score`);
      const { data } = await api.post(`/proposals/${proposalId}/ai-score`);
      setScore(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI re-score failed");
    } finally { setBusy(false); }
  };
  if (!score) {
    return (
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={run} disabled={busy}
          className={`px-3 py-1.5 text-xs font-mono border ${busy
            ? "border-white/10 text-gray-500"
            : "border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00]/10"}`}
          data-testid={`proposal-ai-score-btn-${proposalId}`}>
          {busy ? "scoring…" : "▲ AI score this proposal"}
        </button>
        <span className="text-[10px] font-mono text-gray-500">Claude Sonnet 4.6 · one-shot rubric · cached after first run</span>
      </div>
    );
  }
  const scores = [
    { k: "technical",  v: score.technical_fit_0_10 },
    { k: "timeline",   v: score.timeline_realism_0_10 },
    { k: "cost",       v: score.cost_reasonability_0_10 },
  ];
  const colour = (v) => v >= 8 ? "#7CFF6B" : v >= 5 ? "#FFCC00" : "#FF3B30";
  return (
    <div className="mt-3 border border-[#FFCC00]/30 bg-[#FFCC00]/5 p-3"
         data-testid={`proposal-ai-score-card-${proposalId}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="label-tag text-[#FFCC00]">// AI rubric · {score.model}</div>
        <button type="button" onClick={reRun} disabled={busy}
          className="text-[10px] font-mono text-gray-400 hover:text-white underline"
          data-testid={`proposal-ai-score-rerun-${proposalId}`}>
          {busy ? "…" : "re-score"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {scores.map((s) => (
          <div key={s.k} data-testid={`proposal-ai-score-${s.k}-${proposalId}`}>
            <div className="text-[9px] font-mono text-gray-400 uppercase">{s.k}</div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-display font-bold" style={{ color: colour(s.v) }}>{s.v}</span>
              <span className="text-[10px] font-mono text-gray-500">/ 10</span>
            </div>
            <div className="mt-1 h-1 bg-white/5">
              <div className="h-1" style={{ width: `${s.v * 10}%`, backgroundColor: colour(s.v) }} />
            </div>
          </div>
        ))}
      </div>
      {(score.risks || []).length > 0 && (
        <div className="space-y-1">
          <div className="text-[9px] font-mono text-gray-400 uppercase">risks to verify</div>
          <ul className="text-xs text-gray-200 space-y-0.5">
            {score.risks.map((r, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`proposal-ai-score-risk-${i}-${proposalId}`}>
                <span className="text-[#FF3B30] mt-0.5">•</span> <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {score.recommendation && (
        <div className="mt-2 pt-2 border-t border-white/10 text-xs font-mono text-[#7CFF6B]"
             data-testid={`proposal-ai-score-rec-${proposalId}`}>
          <span className="text-gray-400">recommendation · </span>{score.recommendation}
        </div>
      )}
    </div>
  );
}


/* v3.2.6 · ProposalCard — single proposal detail card (used inside the
   leaderboard for both top-3 and the collapsed "others" list). */
function ProposalCard({ p, jobStatus, onDecide, rank, whyDiff, highlight, currency, viewerRole }) {
  const rankBadge = rank === 1
    ? { icon: <Trophy size={16} weight="fill" />, color: "#FFD700", label: "1st" }
    : rank === 2
    ? { icon: <Medal size={16} weight="fill" />,  color: "#C0C0C0", label: "2nd" }
    : rank === 3
    ? { icon: <Medal size={16} weight="fill" />,  color: "#CD7F32", label: "3rd" }
    : null;

  const border = p.status === "accepted"
    ? "border-emerald-400/40 bg-emerald-400/5"
    : p.status === "rejected"
    ? "border-white/10 bg-white/5 opacity-60"
    : highlight
    ? "border-[#FFD700]/40 bg-[#FFD700]/[0.03]"
    : "border-white/10 bg-[#12151C]";

  return (
    <div className={`border p-4 ${border}`} data-testid={`proposal-row-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {rankBadge && (
            <div className="flex flex-col items-center pt-0.5"
                 style={{ color: rankBadge.color }}
                 data-testid={`proposal-rank-badge-${p.id}`}>
              {rankBadge.icon}
              <span className="text-[9px] font-mono mt-0.5">{rankBadge.label}</span>
            </div>
          )}
          <div>
            <div className="font-display text-lg font-bold">
              {/* v3.2.63 · Wrap the PE name in a link to their public
                   profile so the client can vet the person behind the
                   bid. Incognito PEs get the masked handle rendered
                   as plain text (no link) until the client awards
                   them — mirrors the reveal-on-award rule. */}
              {p.pe_incognito ? (
                <span className="text-white cursor-not-allowed"
                      title="This engineer has hidden their identity — you'll see their profile once you award them the job."
                      data-testid={`proposal-pe-name-${p.id}`}>
                  {p.pe_name}
                </span>
              ) : (
                <Link to={`/directory/pes/${p.pe_id}`}
                       className="text-white hover:text-[#00E5FF] hover:underline transition"
                       target="_blank"
                       rel="noreferrer"
                       data-testid={`proposal-pe-name-${p.id}`}>
                  {p.pe_name}
                </Link>
              )}
              <VerifiedBadge verified={p.pe_email_verified} size={15} />
            </div>
            <div className="text-xs text-gray-400 font-mono mt-1">
              {fmtUsdWithLocal(p.total_fee_usd, currency, p.fx_lock)} · {p.timeline_weeks} weeks · {(p.milestones || []).length} milestones · {(p.deliverables || []).length} deliverables
            </div>
            {p.fx_lock && String(p.fx_lock.currency || "").toUpperCase() === String(currency || "").toUpperCase() && (
              <div className="text-[10px] font-mono text-[#FFCC00]/80 mt-1 inline-flex items-center gap-1"
                   data-testid={`proposal-fx-lock-${p.id}`}>
                🔒 rate locked at {String(p.fx_lock.locked_at || "").slice(0, 10)} · 1 USD = {Number(p.fx_lock.rate).toFixed(4)} {p.fx_lock.currency}
              </div>
            )}
          </div>
        </div>
        <div className={`text-[10px] font-mono uppercase tracking-wider ${p.status === "accepted" ? "text-emerald-400" : p.status === "rejected" ? "text-[#FF8A85]" : "text-[#00E5FF]"}`}>
          {p.status}
        </div>
      </div>
      <p className="mt-2 text-sm text-gray-200 line-clamp-3">{p.cover_letter}</p>

      {(p.itemized_scope || []).length > 0 && (
        <div className="mt-3 border border-white/10 overflow-hidden" data-testid={`proposal-scope-view-${p.id}`}>
          <div className="grid grid-cols-12 bg-black/50 text-[10px] font-mono uppercase tracking-wider text-gray-500 px-2 py-1">
            <div className="col-span-6">Item</div>
            <div className="col-span-2 text-right">Jr/Int</div>
            <div className="col-span-2 text-right">Sr</div>
            <div className="col-span-2 text-right">Cost</div>
          </div>
          {p.itemized_scope.map((r, i) => (
            <div key={i} className="grid grid-cols-12 items-center px-2 py-0.5 border-t border-white/5 text-[11px] font-mono">
              <div className="col-span-6 text-gray-300 truncate">{r.item}</div>
              <div className="col-span-2 text-right text-gray-400">{r.jr_hrs || 0}</div>
              <div className="col-span-2 text-right text-gray-400">{r.sr_hrs || 0}</div>
              <div className="col-span-2 text-right text-emerald-400">${(Number(r.cost) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-3 text-[10px] font-mono">
        <a href={`${getBackendUrl()}/api/proposals/${p.id}/pdf`}
          target="_blank" rel="noreferrer"
          className="text-[#00E5FF] hover:underline inline-flex items-center gap-1"
          data-testid={`proposal-pdf-${p.id}`}>
          <FilePdf size={12} weight="fill" /> download PDF
        </a>
      </div>

      {/* v3.2.15 · Milestone status board — visible on awarded proposals only. */}
      {p.status === "accepted" && (p.milestones || []).length > 0 && (
        <MilestoneBoard jobId={p.job_id} milestones={p.milestones} viewerRole={viewerRole} />
      )}

      {whyDiff && (
        <div className="mt-3 border-l-2 border-[#FFD700] bg-[#FFD700]/5 px-3 py-2 text-xs text-gray-100"
             data-testid={`proposal-why-diff-${p.id}`}>
          <span className="font-mono text-[9px] uppercase text-[#FFD700] mr-1">// why they beat the rest ·</span>
          {whyDiff}
        </div>
      )}

      <AiScoreSection proposalId={p.id} initialScore={p.ai_score} />
      {(p.attachments || []).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-mono text-gray-500 mb-1">// attachments ({p.attachments.length})</div>
          <AttachmentUploader
            endpoint="/proposals/upload-attachment"
            kind="proposal"
            value={p.attachments}
            onChange={() => {}}
            testid={`proposal-row-attach-${p.id}`}
            readOnly />
        </div>
      )}
      {jobStatus === "open" && p.status === "submitted" && (
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={() => onDecide(p.id, "reject")}
            className="border border-[#FF3B30]/60 text-[#FF3B30] px-3 py-1.5 text-xs font-mono hover:bg-[#FF3B30]/10 inline-flex items-center gap-1"
            data-testid={`proposal-reject-${p.id}`}><XCircle size={12} weight="fill" /> Reject</button>
          <button onClick={() => onDecide(p.id, "accept")}
            className="border border-emerald-400/60 text-emerald-400 px-3 py-1.5 text-xs font-mono hover:bg-emerald-400/10 inline-flex items-center gap-1"
            data-testid={`proposal-accept-${p.id}`}><CheckCircle size={12} weight="fill" /> Accept</button>
        </div>
      )}
    </div>
  );
}

/* v3.2.6 · ProposalLeaderboard — AI-ranked proposal list for the client.
   * Top-3 pinned with rank badges and comparative "why they beat the rest".
   * Collapsible "Others" list below.
   * Uses POST /api/jobs/{id}/ai-leaderboard which auto-scores any un-scored
     proposal on-demand, then computes a weighted composite. */
function ProposalLeaderboard({ jobId, jobStatus, proposals, onDecide, currency, viewerRole }) {
  const [board, setBoard] = useState(null);       // { ranking, top_diffs, verdict, weights }
  const [busy, setBusy]   = useState(false);
  const [showOthers, setShowOthers] = useState(false);
  const [expandFlat, setExpandFlat] = useState(false);

  const run = async (refresh = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/jobs/${jobId}/ai-leaderboard${refresh ? "?refresh=true" : ""}`,
      );
      setBoard(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Leaderboard failed");
    } finally { setBusy(false); }
  };

  // Fallback flat rendering when the leaderboard hasn't been run yet.
  if (!board) {
    return (
      <section className="mt-8" data-testid="job-detail-proposals">
        <div className="flex items-center justify-between mb-3">
          <div className="label-tag">// proposals ({proposals.length})</div>
          {proposals.length >= 1 && (
            <button onClick={() => run(false)} disabled={busy}
              className={`px-3 py-1.5 text-xs font-mono border inline-flex items-center gap-1 ${busy
                ? "border-white/10 text-gray-500"
                : "border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10"}`}
              data-testid="proposal-leaderboard-run">
              <Trophy size={14} weight="fill" /> {busy ? "ranking with AI…" : "AI leaderboard"}
            </button>
          )}
        </div>
        <div className="space-y-3">
          {(expandFlat ? proposals : proposals.slice(0, 3)).map((p) => (
            <ProposalCard key={p.id} p={p} jobStatus={jobStatus} onDecide={onDecide} currency={currency} viewerRole={viewerRole} />
          ))}
          {proposals.length > 3 && !expandFlat && (
            <button onClick={() => setExpandFlat(true)}
              className="w-full text-xs font-mono text-gray-400 hover:text-white border border-dashed border-white/10 py-2"
              data-testid="proposal-flat-expand">
              show {proposals.length - 3} more proposal{proposals.length - 3 === 1 ? "" : "s"} · click <b>AI leaderboard</b> to rank
            </button>
          )}
        </div>
      </section>
    );
  }

  const byId = Object.fromEntries(proposals.map((p) => [p.id, p]));
  const diffById = Object.fromEntries((board.top_diffs || []).map((d) => [d.proposal_id, d.why_they_beat]));
  const top3   = board.ranking.slice(0, 3);
  const others = board.ranking.slice(3);
  const W = board.weights || {};

  return (
    <section className="mt-8" data-testid="job-detail-proposals">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tag">// AI leaderboard · {board.ranking.length} proposal{board.ranking.length === 1 ? "" : "s"}</div>
        <button onClick={() => run(true)} disabled={busy}
          className="text-[10px] font-mono text-gray-400 hover:text-white inline-flex items-center gap-1"
          data-testid="proposal-leaderboard-refresh">
          <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} />
          {busy ? "refreshing…" : "refresh"}
        </button>
      </div>

      {/* Weights + verdict banner */}
      <div className="mb-4 border border-[#FFD700]/30 bg-[#FFD700]/[0.04] p-3"
           data-testid="proposal-leaderboard-header">
        <div className="text-[10px] font-mono text-gray-400 mb-1">
          weighted rubric ·{" "}
          <span className="text-[#FFD700]">tech {Math.round((W.technical_fit_0_10 || 0) * 100)}%</span> ·{" "}
          <span className="text-[#FFD700]">cost {Math.round((W.cost_reasonability_0_10 || 0) * 100)}%</span> ·{" "}
          <span className="text-[#FFD700]">timeline {Math.round((W.timeline_realism_0_10 || 0) * 100)}%</span>
          <span className="text-gray-500"> · {board.model}</span>
        </div>
        {board.verdict && (
          <div className="text-sm text-gray-100" data-testid="proposal-leaderboard-verdict">
            <span className="text-[#FFD700] font-mono text-[10px] mr-1">// verdict ·</span>
            {board.verdict}
          </div>
        )}
      </div>

      {/* Top-3 podium */}
      <div className="space-y-3" data-testid="proposal-leaderboard-top3">
        {top3.map((r) => {
          const p = byId[r.proposal_id];
          if (!p) return null;
          return (
            <div key={r.proposal_id} className="relative">
              <CompositeBar composite={r.composite_score} sub={r.sub_scores} />
              <ProposalCard p={p} jobStatus={jobStatus} onDecide={onDecide}
                rank={r.rank} whyDiff={diffById[r.proposal_id]}
                highlight={r.rank === 1} currency={currency} viewerRole={viewerRole} />
            </div>
          );
        })}
      </div>

      {/* Others — collapsible */}
      {others.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowOthers((v) => !v)}
            className="w-full text-xs font-mono text-gray-400 hover:text-white border border-dashed border-white/10 py-2 inline-flex items-center justify-center gap-1"
            data-testid="proposal-leaderboard-toggle-others">
            {showOthers ? <CaretUp size={12} /> : <CaretDown size={12} />}
            {showOthers ? "hide" : "show"} {others.length} more proposal{others.length === 1 ? "" : "s"}
          </button>
          {showOthers && (
            <div className="mt-3 space-y-3" data-testid="proposal-leaderboard-others">
              {others.map((r) => {
                const p = byId[r.proposal_id];
                if (!p) return null;
                return (
                  <div key={r.proposal_id}>
                    <CompositeBar composite={r.composite_score} sub={r.sub_scores} muted />
                    <ProposalCard p={p} jobStatus={jobStatus} onDecide={onDecide} rank={r.rank} currency={currency} viewerRole={viewerRole} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* Compact 0..10 composite bar shown above each ranked proposal card. */
function CompositeBar({ composite, sub, muted }) {
  const c = (v) => (v >= 8 ? "#7CFF6B" : v >= 5 ? "#FFCC00" : "#FF3B30");
  const opacity = muted ? "opacity-60" : "";
  return (
    <div className={`mb-1 flex items-center gap-3 text-[10px] font-mono ${opacity}`}>
      <span className="text-gray-500">composite</span>
      <span className="font-display text-lg font-bold" style={{ color: c(composite) }}>
        {composite.toFixed(1)}
      </span>
      <span className="text-gray-500">/ 10</span>
      <div className="flex items-center gap-2 ml-2">
        <SubPill label="tech"     v={sub.technical_fit} />
        <SubPill label="cost"     v={sub.cost_reasonability} />
        <SubPill label="timeline" v={sub.timeline_realism} />
      </div>
    </div>
  );
}

function SubPill({ label, v }) {
  const c = v >= 8 ? "#7CFF6B" : v >= 5 ? "#FFCC00" : "#FF3B30";
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-gray-500">{label}</span>
      <span style={{ color: c }}>{v}</span>
    </span>
  );
}

/* v3.2.15 · MilestoneBoard — status pills + client approve/reject actions.
   Rendered only inside the accepted-proposal card. Statuses:
     • pending    → grey — nothing submitted yet
     • submitted  → cyan — deliverable filename referenced this MS
     • released   → emerald — client approved OR 7-day timeout auto-released. */

// v3.2.19 · MilestoneTimelineChart — horizontal 7-day bar per submitted
// milestone with a moving "now" marker. Grey → gold → red intensity
// tracks remaining time. Re-renders every 60 s from the parent tick.
function MilestoneTimelineChart({ milestones }) {
  const submitted = (milestones || [])
    .map((m, i) => ({ ...m, idx: i }))
    .filter((m) => m.status === "submitted" && m.submitted_at);
  if (submitted.length === 0) return null;

  const WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const now = Date.now();

  return (
    <div className="border-t border-white/10 bg-black/20 px-3 py-3"
         data-testid="milestone-timeline-chart">
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-2">
        // auto-release timeline · 7-day window
      </div>
      <div className="space-y-2">
        {submitted.map((m) => {
          const submittedMs = new Date(m.submitted_at).getTime();
          if (Number.isNaN(submittedMs)) return null;
          const elapsed = Math.max(0, now - submittedMs);
          const clamped = Math.min(elapsed, WINDOW_MS);
          const pctElapsed = (clamped / WINDOW_MS) * 100;
          const remainingMs = WINDOW_MS - elapsed;
          const remainingH = Math.max(0, remainingMs / (60 * 60 * 1000));
          const state = remainingH < 24 ? "red"
                      : remainingH < 48 ? "gold" : "green";
          const barColor = state === "red"   ? "bg-[#FF8A85]/70 border-[#FF8A85]"
                          : state === "gold" ? "bg-[#FFCC00]/70 border-[#FFCC00]"
                          :                    "bg-emerald-500/50 border-emerald-500";
          const label = remainingH < 24
              ? `${Math.max(0, Math.ceil(remainingH))}h left`
              : `${Math.max(0, Math.floor(remainingH / 24))}d left`;
          return (
            <div key={m.idx} className="flex items-center gap-2"
                 data-testid={`milestone-timeline-row-${m.idx}`}>
              <div className="w-16 text-[10px] font-mono text-gray-400 shrink-0">
                MS{m.idx + 1}
              </div>
              <div className="relative flex-1 h-5 bg-white/5 border border-white/10 overflow-hidden">
                {/* Filled elapsed portion */}
                <div
                  className={`absolute inset-y-0 left-0 border-r-2 ${barColor}`}
                  style={{ width: `${pctElapsed}%` }}
                />
                {/* Now marker — thin cyan line */}
                <div
                  className="absolute inset-y-0 w-px bg-[#00E5FF] shadow-[0_0_6px_#00E5FF]"
                  style={{ left: `calc(${pctElapsed}% - 0.5px)` }}
                  data-testid={`milestone-timeline-marker-${m.idx}`}
                />
                {/* End-of-window pip */}
                <div className="absolute inset-y-0 right-0 w-px bg-white/30" />
              </div>
              <div className={`w-20 shrink-0 text-right text-[10px] font-mono tabular-nums ${
                state === "red"   ? "text-[#FF8A85]" :
                state === "gold" ? "text-[#FFCC00]" : "text-emerald-400"
              }`}
                   data-testid={`milestone-timeline-label-${m.idx}`}>
                {label}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[9px] font-mono text-gray-500">
        <span>submitted</span>
        <span>auto-release &rarr;</span>
      </div>
    </div>
  );
}



function MilestoneBoard({ jobId, milestones, viewerRole }) {
  const [rows, setRows] = useState(milestones || []);
  const [busy, setBusy] = useState(null);   // index currently posting
  const [, forceTick] = useState(0);        // v3.2.18 · re-render every 60s so countdown ticks live
  // v3.2.32 · Refund modal state.
  const [refundModal, setRefundModal] = useState(null);   // { idx, m, eligibility }
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);

  useEffect(() => { setRows(milestones || []); }, [milestones]);

  useEffect(() => {
    // v3.2.18 · Tick every 60 s so `Xd left` / `Xh left` / grey→gold→red
    // transitions happen without the user needing to refresh.
    const anySubmitted = (milestones || []).some((m) => m.status === "submitted");
    if (!anySubmitted) return;
    const t = setInterval(() => forceTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(t);
  }, [milestones]);

  // v3.2.18 · Notification deep-link — if URL hash is #milestone-{i}, scroll to
  // and briefly highlight that row when it mounts.
  useEffect(() => {
    const m = /^#milestone-(\d+)$/.exec(window.location.hash || "");
    if (!m) return;
    const idx = Number(m[1]);
    const el = document.querySelector(`[data-testid="milestone-row-${idx}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[#00E5FF]", "ring-offset-0", "bg-[#00E5FF]/5");
    const t = setTimeout(() => {
      el.classList.remove("ring-2", "ring-[#00E5FF]", "ring-offset-0", "bg-[#00E5FF]/5");
    }, 2400);
    return () => clearTimeout(t);
  }, [rows.length]);

  const decide = async (idx, action) => {
    setBusy(idx);
    try {
      const { data } = await api.post(`/jobs/${jobId}/milestones/${idx + 1}/${action}`);
      setRows((xs) => xs.map((m, i) => (i === idx ? data.milestone : m)));
      toast.success(action === "approve" ? `MS${idx + 1} released.` : `MS${idx + 1} sent back.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `${action} failed`);
    } finally { setBusy(null); }
  };

  // v3.2.31 · Client-side "Pay MS{i}" — kicks off a Stripe Checkout
  // session and redirects the tab to Stripe's hosted page. The user
  // returns to /payment/success (or /cancel) which resolves the state.
  const payMilestone = async (idx) => {
    setBusy(idx);
    try {
      const { data } = await api.post("/payments/milestone-checkout", {
        job_id:        jobId,
        milestone_idx: idx + 1,
        origin_url:    window.location.origin,
      });
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
      } else {
        toast.error("Could not start checkout.");
        setBusy(null);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment failed to start.");
      setBusy(null);
    }
  };

  // v3.2.32 · Refund flow — fetch eligibility, open the modal.
  const openRefundModal = async (idx, m) => {
    if (!m.payment_session_id) return;
    setBusy(idx);
    try {
      const { data } = await api.get(`/payments/${m.payment_session_id}/refund-eligibility`);
      setRefundModal({ idx, m, eligibility: data });
      setRefundReason("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not check refund eligibility.");
    } finally { setBusy(null); }
  };

  const submitRefundRequest = async () => {
    if (!refundModal || !refundReason.trim()) {
      toast.error("Please describe why you want a refund.");
      return;
    }
    setRefundBusy(true);
    try {
      const sid = refundModal.m.payment_session_id;
      const { data } = await api.post(`/payments/${sid}/refund-request`,
        { reason: refundReason.trim() });
      if (data.mode === "self_serve") {
        toast.success("Refund processed. Milestone reset.");
        // Optimistic update — mark row as refunded.
        setRows((xs) => xs.map((m, i) =>
          i === refundModal.idx
            ? { ...m, paid_at: null, payment_status: "refunded",
                  refund_reason: refundReason.trim() }
            : m));
      } else if (data.mode === "already_pending") {
        toast.info("A refund request is already pending admin review.");
      } else {
        toast.success("Refund request sent to admin for review.");
      }
      setRefundModal(null);
      setRefundReason("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refund request failed.");
    } finally { setRefundBusy(false); }
  };

  const pill = (status) => {
    if (status === "released")
      return { text: "released", cls: "border-emerald-400/40 text-emerald-400 bg-emerald-400/10" };
    if (status === "submitted")
      return { text: "submitted", cls: "border-[#00E5FF]/40 text-[#00E5FF] bg-[#00E5FF]/10" };
    return       { text: "pending",   cls: "border-white/10 text-gray-400" };
  };

  const expiringSoon = rows.filter((m) => {
    if (m.status !== "submitted" || !m.submitted_at) return false;
    const ms = new Date(m.submitted_at).getTime();
    if (Number.isNaN(ms)) return false;
    const remaining = ms + 7 * 24 * 60 * 60 * 1000 - Date.now();
    return remaining > 0 && remaining < 48 * 60 * 60 * 1000;
  }).length;

  return (
    <div className="mt-3 border border-white/10" data-testid="milestone-board">
      <div className="px-3 py-1.5 bg-black/40 text-[10px] font-mono uppercase tracking-wider text-gray-500 flex items-center justify-between">
        <div>
          // milestones ({rows.length}) · <span className="text-gray-400 lowercase normal-case">upload a file named <b>MS1_*</b>, <b>Milestone 1 *</b> to auto-submit MS1</span>
        </div>
        {expiringSoon > 0 && (
          <span className="normal-case tracking-normal border border-[#FFCC00]/40 text-[#FFCC00] bg-[#FFCC00]/10 px-1.5 py-0.5 text-[10px]"
                data-testid="milestone-expiring-badge">
            ⏳ {expiringSoon} expiring in &lt; 48h
          </span>
        )}
      </div>
      {/* v3.2.19 · Timeline chart — one 7-day bar per submitted milestone
          with a moving "now" marker that ticks every 60 s. */}
      <MilestoneTimelineChart milestones={rows} />
      {rows.map((m, i) => {
        const p = pill(m.status || "pending");
        const isClient = viewerRole === "client" || viewerRole === "admin";
        // v3.2.16 · Countdown until the 7-day auto-release kicks in.
        // Only visible for milestones sitting in `submitted`.
        let countdown = null;
        if (m.status === "submitted" && m.submitted_at) {
          const submittedMs = new Date(m.submitted_at).getTime();
          if (!Number.isNaN(submittedMs)) {
            const releaseMs = submittedMs + 7 * 24 * 60 * 60 * 1000;
            const remainingMs = releaseMs - Date.now();
            const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
            const hours = Math.max(0, Math.ceil(remainingMs / (60 * 60 * 1000)));
            const overdue = remainingMs <= 0;
            const urgent  = !overdue && remainingMs < 48 * 60 * 60 * 1000;
            countdown = {
              text: overdue
                ? "auto-release imminent"
                : days >= 1
                  ? `${days}d left to review`
                  : `${hours}h left`,
              cls: overdue
                ? "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10"
                : urgent
                  ? "text-[#FFCC00] border-[#FFCC00]/40 bg-[#FFCC00]/10"
                  : "text-gray-400 border-white/10",
            };
          }
        }
        return (
          <div key={i}
            className="grid grid-cols-12 items-center gap-2 px-3 py-2 border-t border-white/5 text-xs"
            data-testid={`milestone-row-${i}`}>
            <div className="col-span-1 font-mono text-gray-500">MS{i + 1}</div>
            <div className="col-span-5">
              <div className="text-white truncate">{m.title || <span className="text-gray-500 italic">(untitled)</span>}</div>
              {m.last_file_name && (
                <div className="text-[10px] text-gray-500 font-mono truncate">📎 {m.last_file_name}</div>
              )}
              {countdown && (
                <div className={`mt-1 inline-block border px-1.5 py-0.5 text-[9px] font-mono ${countdown.cls}`}
                     data-testid={`milestone-countdown-${i}`}>
                  ⏳ {countdown.text}
                </div>
              )}
            </div>
            <div className="col-span-2 text-right text-emerald-400 font-mono">
              ${Number(m.amount_usd || 0).toLocaleString()}
            </div>
            <div className="col-span-2">
              <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-mono ${p.cls}`}
                    data-testid={`milestone-status-${i}`}>
                {p.text}
              </span>
              {m.released_by === "auto" && (
                <span className="ml-1 text-[9px] text-[#FFCC00] font-mono">auto</span>
              )}
            </div>
            <div className="col-span-2 flex justify-end gap-1">
              {isClient && m.status === "submitted" && (
                <>
                  <button onClick={() => decide(i, "reject")}
                    disabled={busy === i}
                    className="border border-[#FF3B30]/60 text-[#FF3B30] px-2 py-0.5 text-[10px] font-mono hover:bg-[#FF3B30]/10 disabled:opacity-40"
                    data-testid={`milestone-reject-${i}`}>reject</button>
                  <button onClick={() => decide(i, "approve")}
                    disabled={busy === i}
                    className="border border-emerald-400/60 text-emerald-400 px-2 py-0.5 text-[10px] font-mono hover:bg-emerald-400/10 disabled:opacity-40"
                    data-testid={`milestone-approve-${i}`}>approve</button>
                </>
              )}
              {/* v3.2.31 · Client pays a released, unpaid milestone via Stripe Checkout. */}
              {isClient && m.status === "released" && !m.paid_at && (
                <button onClick={() => payMilestone(i)}
                  disabled={busy === i}
                  className="border border-[#00E5FF]/60 text-[#00E5FF] px-2 py-0.5 text-[10px] font-mono hover:bg-[#00E5FF]/10 disabled:opacity-40"
                  data-testid={`milestone-pay-${i}`}
                  title="Pay this milestone via Stripe">
                  pay ${Number(m.amount_usd || 0).toLocaleString()}
                </button>
              )}
              {m.paid_at && (
                <span className="border border-emerald-400/60 text-emerald-400 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-mono"
                      data-testid={`milestone-paid-badge-${i}`}
                      title={`Paid ${new Date(m.paid_at).toLocaleDateString()}`}>
                  ✓ paid
                </span>
              )}
              {/* v3.2.32 · Client can request a refund on a paid milestone. */}
              {isClient && m.paid_at && m.payment_session_id && (
                <button onClick={() => openRefundModal(i, m)}
                  disabled={busy === i}
                  className="border border-white/20 text-gray-300 hover:text-white hover:border-white/40 px-2 py-0.5 text-[10px] font-mono disabled:opacity-40"
                  data-testid={`milestone-refund-btn-${i}`}
                  title="Request a refund for this milestone">
                  refund
                </button>
              )}
              {/* v3.2.32 · Show refunded state. */}
              {m.payment_status === "refunded" && !m.paid_at && (
                <span className="border border-gray-500/40 text-gray-400 bg-black/30 px-2 py-0.5 text-[10px] font-mono"
                      data-testid={`milestone-refunded-badge-${i}`}
                      title={m.refund_reason ? `Refunded — ${m.refund_reason}` : "Refunded"}>
                  ↻ refunded
                </span>
              )}
            </div>
          </div>
        );
      })}
      {/* v3.2.32 · Refund request modal */}
      {refundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
             onClick={() => !refundBusy && setRefundModal(null)}
             data-testid="refund-modal">
          <div className="w-full max-w-lg bg-[#0A0C10] border border-white/20 p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="label-tag">// refund request</div>
            <h2 className="font-display text-2xl font-black mt-1">
              MS{refundModal.idx + 1} · {refundModal.m.title}
            </h2>
            <div className="text-xs font-mono text-gray-400 mt-1">
              Amount · <span className="text-white">${Number(refundModal.m.amount_usd || 0).toLocaleString()} USD</span>
              {refundModal.m.paid_at && (
                <> · paid {new Date(refundModal.m.paid_at).toLocaleDateString()}</>
              )}
            </div>

            {refundModal.eligibility.has_pending_request ? (
              <div className="mt-5 border border-[#FFCC00]/40 bg-[#FFCC00]/5 p-3 text-sm text-[#FFCC00] font-mono"
                   data-testid="refund-pending-notice">
                A refund request is already pending admin review for this milestone.
              </div>
            ) : refundModal.eligibility.can_self_refund ? (
              <div className="mt-5 border border-emerald-400/40 bg-emerald-400/5 p-3 text-sm text-emerald-300 font-mono"
                   data-testid="refund-self-serve-notice">
                Within 24 h — this refund will process immediately.
                {refundModal.eligibility.grace_ends_at && (
                  <div className="text-[10px] text-emerald-400/70 mt-1">
                    grace ends · {new Date(refundModal.eligibility.grace_ends_at).toLocaleString()}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5 border border-[#00E5FF]/40 bg-[#00E5FF]/5 p-3 text-sm text-[#00E5FF] font-mono"
                   data-testid="refund-admin-queue-notice">
                24-hour grace has passed — this request goes to admin for review.
              </div>
            )}

            {!refundModal.eligibility.has_pending_request && (
              <>
                <label className="block text-xs font-mono text-gray-400 mt-4">
                  Why do you want this refund?
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder="Describe the issue — this is shared with admin and the PE."
                  className="mt-2 w-full bg-black/50 border border-white/10 focus:border-[#00E5FF] p-3 text-sm text-white outline-none"
                  data-testid="refund-reason-input"
                />
                <div className="mt-1 text-[10px] font-mono text-gray-500 text-right">
                  {refundReason.length} / 1000
                </div>
              </>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setRefundModal(null)}
                disabled={refundBusy}
                className="text-xs font-mono text-gray-400 hover:text-white px-3 py-2 disabled:opacity-40"
                data-testid="refund-cancel-btn">
                cancel
              </button>
              {!refundModal.eligibility.has_pending_request && (
                <button onClick={submitRefundRequest}
                  disabled={refundBusy || !refundReason.trim()}
                  className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-4 py-2 ${
                    refundModal.eligibility.can_self_refund
                      ? "bg-emerald-400 text-black hover:bg-emerald-400/80"
                      : "bg-[#00E5FF] text-black hover:bg-[#00E5FF]/80"
                  } disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed`}
                  data-testid="refund-submit-btn">
                  {refundBusy
                    ? "processing…"
                    : refundModal.eligibility.can_self_refund
                      ? "refund now"
                      : "submit request"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

